import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader as LoaderIcon, X as CloseIcon } from "lucide-react";
import type { WhatsAppTemplateDto } from "@multi-chat/shared";
import { Badge } from "../../../../components/ui/badge/index.js";
import { Button } from "../../../../components/ui/button/index.js";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "../../../../components/ui/sheet/index.js";
import { cn } from "../../../../lib/utils.js";
import { WhatsAppTemplatePreview } from "../../WhatsAppTemplatePreview/index.js";
import {
  templateEditorDescriptionKey,
  templateEditorMobileOrderClass,
  templateEditorPreviewPresentation,
  templateEditorSubmitLabelKey,
  templateEditorTitleKey,
} from "../../template-editor-presentation.js";
import {
  templateListStatusBadgeVariant,
  templateListStatusLabelKey,
} from "../../template-list-presentation.js";
import { isCategoryEditable } from "../template-management-status.js";
import { mapTemplateManagementError } from "../template-management-errors.js";
import {
  buildCreateTemplateRequest,
  buildEditTemplateRequest,
  createEmptyTemplateFormState,
  isTemplateEditorDraftDirty,
  templateDtoToFormState,
  validateTemplateForm,
  type TemplateFormState,
} from "../template-management-form.js";
import {
  buildDuplicateTemplateFormState,
  resolveTemplateEditSheetPresentation,
} from "../template-editor-support.js";
import { TemplateForm } from "../TemplateForm/index.js";
import { TemplateCreationWizard } from "../TemplateCreationWizard/index.js";
import { TemplateUnsavedChangesDialog } from "../TemplateUnsavedChangesDialog.js";

const TEMPLATE_EDITOR_SHEET_CLASS =
  "start-[var(--app-rail-width,0px)] end-0 h-dvh max-h-dvh w-auto max-w-none gap-0 overflow-hidden overflow-x-hidden overflow-y-hidden bg-surface-elevated p-0 text-foreground data-[state=closed]:animate-sheet-out-end rtl:data-[state=closed]:animate-sheet-out-start";

export type TemplateManagementSheetMode =
  | { kind: "create" }
  | { kind: "duplicate"; template: WhatsAppTemplateDto }
  | { kind: "edit"; template: WhatsAppTemplateDto };

export interface TemplateManagementSheetProps {
  open: boolean;
  mode: TemplateManagementSheetMode | null;
  unreachableMessage: string;
  onOpenChange: (open: boolean) => void;
  onCreate: (
    payload: ReturnType<typeof buildCreateTemplateRequest>,
    headerImage?: File | null,
  ) => Promise<void>;
  onEdit: (
    templateId: number,
    payload: ReturnType<typeof buildEditTemplateRequest>,
  ) => Promise<void>;
  callingManagerUrl?: string | null;
  organizationId?: number;
}

const spinClass = "animate-[spin_0.8s_linear_infinite]";

export function TemplateManagementSheet({
  open,
  mode,
  unreachableMessage,
  onOpenChange,
  onCreate,
  onEdit,
  callingManagerUrl = null,
  organizationId,
}: TemplateManagementSheetProps): React.JSX.Element {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const previewTitleId = useId();
  const [formState, setFormState] = useState<TemplateFormState>(createEmptyTemplateFormState());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const legacyBaselineRef = useRef<TemplateFormState | null>(null);

  const editPresentation =
    mode?.kind === "edit" ? resolveTemplateEditSheetPresentation(mode.template) : null;
  const useMarketingDefaultEdit = editPresentation === "marketing_default_editor";
  const useUnsupportedEdit = editPresentation === "unsupported";
  const useLegacyEdit = editPresentation === "legacy_form";

  useEffect(() => {
    if (!open || !mode) return;
    setError(null);
    setValidationMessage(null);
    setSubmitting(false);
    if (mode.kind === "create") {
      setFormState(createEmptyTemplateFormState());
      return;
    }
    if (useLegacyEdit && mode.kind === "edit") {
      const baseline = templateDtoToFormState(mode.template);
      legacyBaselineRef.current = baseline;
      setFormState(baseline);
      return;
    }
    legacyBaselineRef.current = null;
  }, [open, mode, useLegacyEdit]);

  useEffect(() => {
    if (!open) {
      document.documentElement.removeAttribute("data-template-editor-open");
      return;
    }
    document.documentElement.setAttribute("data-template-editor-open", "true");
    return () => {
      document.documentElement.removeAttribute("data-template-editor-open");
    };
  }, [open]);

  const sheetMode = mode?.kind ?? "create";
  const templateStatus = mode?.kind === "edit" ? mode.template.status : undefined;

  const presentation = useMemo(
    () => templateEditorPreviewPresentation(formState),
    [formState],
  );

  function mapValidationIssue(): string | null {
    const issues = validateTemplateForm(formState);
    if (issues.length === 0) return null;
    const first = issues[0]!;
    if (first.field === "body" && first.code === "required") {
      return t("whatsapp.templates.manage.validation.bodyRequired");
    }
    if (first.field === "parameterFormat" && first.code === "mixed") {
      return t("whatsapp.templates.manage.validation.mixedVariables");
    }
    if (first.field === "parameterFormat" && first.code === "mismatch") {
      return t("whatsapp.templates.manage.validation.formatMismatch");
    }
    if (first.field === "examples" && first.code === "missing") {
      return t("whatsapp.templates.manage.validation.missingExample", { key: first.key });
    }
    if (first.field === "buttons" && first.code === "interleaved") {
      return t("whatsapp.templates.manage.validation.interleavedButtons");
    }
    if (first.field === "buttons" && first.code === "invalidPhone") {
      return t("whatsapp.templates.manage.validation.invalidPhone");
    }
    return t("whatsapp.templates.manage.validation.generic");
  }

  function dismissEditor(): void {
    if (submitting) return;
    setDiscardOpen(false);
    onOpenChange(false);
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (nextOpen || submitting) return;
  }

  function requestLegacyClose(): void {
    if (submitting) return;
    if (
      legacyBaselineRef.current != null &&
      isTemplateEditorDraftDirty(formState, legacyBaselineRef.current)
    ) {
      setDiscardOpen(true);
      return;
    }
    dismissEditor();
  }

  async function handleLegacySubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!mode || mode.kind !== "edit" || submitting) return;

    const validation = mapValidationIssue();
    if (validation) {
      setValidationMessage(validation);
      return;
    }
    setValidationMessage(null);
    setError(null);
    setSubmitting(true);

    try {
      const includeCategory = isCategoryEditable(mode.template.status);
      await onEdit(
        mode.template.id,
        buildEditTemplateRequest(formState, { includeCategory }),
      );
      dismissEditor();
    } catch (caught) {
      setError(mapTemplateManagementError(t, caught, unreachableMessage));
    } finally {
      setSubmitting(false);
    }
  }

  const formError = validationMessage ?? error;

  return (
    <>
    <Sheet open={open} onOpenChange={handleOpenChange} modal={false}>
      <SheetContent
        className={TEMPLATE_EDITOR_SHEET_CLASS}
        overlayClassName="hidden"
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
        onFocusOutside={(event) => {
          event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
        }}
        side="end"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        {sheetMode === "create" || mode?.kind === "duplicate" ? (
          <>
            <SheetTitle id={titleId} className="sr-only">
              {t(templateEditorTitleKey("create"))}
            </SheetTitle>
            <SheetDescription id={descriptionId} className="sr-only">
              {t(templateEditorDescriptionKey("create"))}
            </SheetDescription>
            <TemplateCreationWizard
              key={
                mode?.kind === "duplicate"
                  ? `duplicate-${mode.template.id}`
                  : open
                    ? "create-open"
                    : "create-closed"
              }
              organizationId={organizationId}
              initialFormState={
                mode?.kind === "duplicate"
                  ? (buildDuplicateTemplateFormState(mode.template) ?? undefined)
                  : undefined
              }
              onClose={dismissEditor}
              callingManagerUrl={callingManagerUrl}
              unreachableMessage={unreachableMessage}
              onSubmit={async (wizardFormState) => {
                await onCreate(
                  buildCreateTemplateRequest(wizardFormState),
                  wizardFormState.headerImageFile,
                );
                dismissEditor();
              }}
            />
          </>
        ) : useMarketingDefaultEdit && mode?.kind === "edit" ? (
          <>
            <SheetTitle id={titleId} className="sr-only">
              {t(templateEditorTitleKey("edit"))}
            </SheetTitle>
            <SheetDescription id={descriptionId} className="sr-only">
              {t(templateEditorDescriptionKey("edit"))}
            </SheetDescription>
            <TemplateCreationWizard
              key={mode.template.id}
              mode="edit"
              organizationId={organizationId}
              templateId={mode.template.id}
              initialFormState={templateDtoToFormState(mode.template)}
              templateStatusLabel={t(templateListStatusLabelKey(mode.template.status))}
              callingManagerUrl={callingManagerUrl}
              unreachableMessage={unreachableMessage}
              onClose={dismissEditor}
              onSubmit={async (wizardFormState) => {
                const includeCategory = isCategoryEditable(mode.template.status);
                await onEdit(
                  mode.template.id,
                  buildEditTemplateRequest(wizardFormState, { includeCategory }),
                );
                dismissEditor();
              }}
            />
          </>
        ) : useUnsupportedEdit && mode?.kind === "edit" ? (
          <div className="flex h-full min-h-0 flex-col">
            <header className="shrink-0 border-b border-line px-4 py-3.5 md:px-6 md:py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="grid min-w-0 flex-1 gap-1">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <SheetTitle
                      id={titleId}
                      className="m-0 text-base leading-tight font-semibold tracking-[-0.015em] text-ink"
                    >
                      {t(templateEditorTitleKey("edit"))}
                    </SheetTitle>
                    <Badge variant={templateListStatusBadgeVariant(mode.template.status)}>
                      {t(templateListStatusLabelKey(mode.template.status))}
                    </Badge>
                  </div>
                  <SheetDescription
                    id={descriptionId}
                    className="m-0 text-[0.8125rem] leading-snug text-ink-muted"
                  >
                    {t("whatsapp.templates.manage.editUnsupportedTitle")}
                  </SheetDescription>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="size-8 shrink-0 p-0"
                  onClick={dismissEditor}
                  aria-label={t("whatsapp.templates.manage.close")}
                >
                  <CloseIcon size={16} aria-hidden="true" />
                </Button>
              </div>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-6">
              <div className="grid max-w-xl gap-3 rounded-lg border border-line bg-surface p-5">
                <p className="m-0 text-sm leading-relaxed text-ink">
                  {t("whatsapp.templates.manage.editUnsupportedBody")}
                </p>
                <p className="m-0 text-sm leading-relaxed text-ink-muted">
                  {t("whatsapp.templates.manage.editUnsupportedHint")}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 justify-end border-t border-line px-4 py-3 md:px-6">
              <Button type="button" variant="outline" onClick={dismissEditor}>
                {t("whatsapp.templates.manage.close")}
              </Button>
            </div>
          </div>
        ) : (
          <form className="flex h-full min-h-0 flex-col" onSubmit={handleLegacySubmit}>
            <header className="shrink-0 border-b border-line px-4 py-3.5 md:px-6 md:py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="grid min-w-0 flex-1 gap-1">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <SheetTitle
                      id={titleId}
                      className="m-0 text-base leading-tight font-semibold tracking-[-0.015em] text-ink"
                    >
                      {t(templateEditorTitleKey("edit"))}
                    </SheetTitle>
                    {templateStatus ? (
                      <Badge variant={templateListStatusBadgeVariant(templateStatus)}>
                        {t(templateListStatusLabelKey(templateStatus))}
                      </Badge>
                    ) : null}
                  </div>
                  <SheetDescription
                    id={descriptionId}
                    className="m-0 text-[0.8125rem] leading-snug text-ink-muted"
                  >
                    {t(templateEditorDescriptionKey("edit"))}
                  </SheetDescription>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="size-8 shrink-0 p-0"
                  onClick={requestLegacyClose}
                  disabled={submitting}
                  aria-label={t("whatsapp.templates.manage.close")}
                >
                  <CloseIcon size={16} aria-hidden="true" />
                </Button>
              </div>
            </header>

            {formError ? (
              <div
                className="shrink-0 border-b border-error/20 bg-error-muted px-4 py-3 text-sm text-error md:px-6"
                role="alert"
              >
                {formError}
              </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-4 py-5 md:px-6 md:py-6">
              <div className="grid grid-cols-1 items-start gap-7 md:grid-cols-[minmax(0,1.58fr)_minmax(17.5rem,22.5rem)] md:gap-x-8 md:gap-y-7">
                <TemplateForm
                  mode="edit"
                  state={formState}
                  onChange={setFormState}
                  templateStatus={templateStatus}
                  disabled={submitting}
                />

                <aside
                  className={cn(
                    templateEditorMobileOrderClass.preview,
                    "grid min-w-0 gap-3 rounded-lg border border-line bg-surface p-3.5 md:sticky md:top-0 md:order-none md:col-start-2 md:row-span-full md:self-start md:p-4",
                  )}
                  aria-labelledby={previewTitleId}
                >
                  <div className="grid gap-1">
                    <h3
                      id={previewTitleId}
                      className="m-0 text-[0.8125rem] font-semibold text-ink"
                    >
                      {t("whatsapp.templates.manage.previewTitle")}
                    </h3>
                    <p className="m-0 text-[0.8rem] leading-snug text-ink-muted">
                      {t("whatsapp.templates.manage.previewHint")}
                    </p>
                  </div>
                  <div className="rounded-md bg-[color-mix(in_srgb,var(--color-surface-muted)_86%,#dcf8c6_14%)] p-3">
                    <WhatsAppTemplatePreview
                      presentation={presentation}
                      contentLanguage={formState.language}
                    />
                  </div>
                </aside>
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-elevated px-4 py-3 shadow-[0_-8px_20px_rgb(15_23_42/0.04)] pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-6">
              <Button
                type="button"
                variant="outline"
                onClick={requestLegacyClose}
                disabled={submitting}
              >
                {t("whatsapp.templates.manage.cancel")}
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <LoaderIcon size={16} className={spinClass} aria-hidden="true" />
                    {t("whatsapp.templates.manage.saving")}
                  </>
                ) : (
                  t(templateEditorSubmitLabelKey("edit"))
                )}
              </Button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
    <TemplateUnsavedChangesDialog
      open={discardOpen}
      onStay={() => setDiscardOpen(false)}
      onDiscard={dismissEditor}
    />
    </>
  );
}
